// ─────────────────────────────────────────────────────────────────────────────
// useRexySocket — the homepage live-telemetry Socket.IO consumer (Chunk 5, desktop).
//
// MODULE SINGLETON: exactly one io() connection for the whole app, created lazily
// on first hook mount (after auth, when window.__rexyToken is populated) and reused
// across every consumer. Mirrors the iOS SocketProvider's "one socket, durable
// broadcasts" model — but the desktop homepage only consumes the three durable
// broadcast streams, so there is no request/response (chat) machinery here.
//
// Subscribes to rexy:vitals / rexy:telemetry / rexy:agents ONLY. rexy:amplitude is
// deliberately NOT consumed here (voice, out of scope for the homepage gauges).
//
// Local TS types mirror docs/rexy_live_events.md byte-for-byte — there is no shared
// importable contract file; if a payload changes in that doc, these types change in
// the same edit.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useState } from 'react';
import { io, Socket } from 'socket.io-client';

// window.__rexyToken is declared global in App.tsx (in-memory token, never localStorage).

// ── Event payload types — mirror docs/rexy_live_events.md ────────────────────
// The DGX Spark (GB10 Grace Blackwell) — the machine Rexy actually runs on.
// EVERY client renders THIS payload: desktop, iOS, tablet, Android, the VPS site are
// all windows into the one core, never separate machines reporting their own hardware.
export type RexyVoiceServiceStatus = 'ok' | 'warming' | 'down';

export interface RexyVoiceServiceLight {
  id: string;
  label: string;
  port: number;
  status: RexyVoiceServiceStatus;
  reachable: boolean | null;
  ready: boolean | null;
  detail: string;
  checked_at: number;
}

export interface RexyVoiceServices {
  overall: RexyVoiceServiceStatus;
  services: RexyVoiceServiceLight[];
  updated_at: string;
}

export interface RexyVitals {
  gpu_util_pct: number | null; // NVML GPU utilisation % — the real inference load
  memory_used_bytes: number | null; // unified pool used (/proc/meminfo, NOT VRAM)
  memory_total_bytes: number | null; // unified pool total — ~121 GiB on this box
  storage_used_bytes: number | null;  // DGX SSD used (df-style read of /)
  storage_total_bytes: number | null; // DGX SSD total — ~4 TB
  connection_ms: number | null; // server-sampled Starlink RTT, ms (replaces latency+signal)
  gpu_temp_c: number | null; // NVML GB10 temperature, °C
  generation_tokens_per_s: number | null; // vLLM generation-counter delta; 0 when idle
  gpu_power_w: number | null; // NVML GPU power draw, W
  voice_services?: RexyVoiceServices; // six OS voice-service probes, current sample only
  server_ts: number; // server epoch ms at emit
}

export interface RexyTelemetry {
  time: string; // "HH:MM:SS" local time of the audit entry
  status: 'OK' | 'INF' | 'WRN' | 'ERR' | 'SEC';
  event: string; // event TYPE, optionally "TYPE→DOMAIN" — never raw audit details
}

export interface RexyAgentEntry {
  name: string; // agent name if logged, else the domain name
  domain: string; // UPPERCASE domain (e.g. "TRADING")
  last_active: string; // ISO-8601 timestamp of most recent activity
}

export interface RexyAgents {
  active: RexyAgentEntry[];
}

// ── Connection config ────────────────────────────────────────────────────────
// Reconnection backoff ceiling (ms). socket.io grows the delay geometrically from
// reconnectionDelay up to this cap.
const RECONNECT_DELAY_MAX_MS = 10_000;

// SAME-ORIGIN, always. REST (App.tsx:38) derives its host from window.location; the
// socket MUST derive from the same place or the two transports split — a LAN device
// hitting DGX-IP:3000 would get REST on the DGX but the socket on the BROWSING DEVICE's
// own localhost (which isn't running Rexy), so live state silently never arrives.
// Deriving both from the page host means whatever window opened Rexy — Electron on
// localhost, a phone/iPad/PC at the DGX's LAN IP, herexy.com — reaches the one core.
//   • prod  → the page host, no explicit port (nginx proxies /socket.io → Flask).
//   • dev   → the page hostname on :5000 (Flask direct), mirroring App.tsx's API_BASE.
// The ws/wss scheme tracks the page protocol so an https page never opens an insecure
// socket. Hardcoding either endpoint recreates the split — the one thing that must not happen.
function socketOrigin(): string {
  // Keyed on the PAGE PROTOCOL, mirroring App.tsx's API_BASE exactly, so REST and the
  // socket never split:
  //   • HTTPS page → wss same-origin on :443 (no port). Covers herexy.com (nginx) and
  //     Tailscale Serve (which path-mounts /socket.io DIRECTLY to Flask). An HTTPS page
  //     cannot open ws:// (mixed content), so wss is mandatory here.
  //   • HTTP page → ws direct to Flask :5000 — the countersigned LAN/Electron path,
  //     unchanged.
  return window.location.protocol === 'https:'
    ? `wss://${window.location.host}`
    : `ws://${window.location.hostname}:5000`;
}

let socket: Socket | null = null;

// The lazily-created app-wide singleton. Token is read from window.__rexyToken at
// creation time (first hook mount), by which point auth has populated it.
export function getRexySocket(): Socket {
  if (socket) return socket;
  socket = io(socketOrigin(), {
    auth: { token: window.__rexyToken },
    // WEBSOCKET FIRST, POLLING BENEATH IT (owner ruling 2026-09-16). This was
    // `['websocket']` alone, and a websocket that could not be established — or one
    // the path dropped mid-stall — had NOTHING to degrade to: the window went straight
    // from live to offline. The ORDER is deliberate and leaves today's happy path
    // unchanged: socket.io reaches for websocket first and only falls back to polling
    // when that fails, so a healthy connection never pays for a polling handshake.
    // A stall becomes a slow moment instead of a drop.
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionDelayMax: RECONNECT_DELAY_MAX_MS,
  });
  return socket;
}

export interface RexySocketState {
  connected: boolean;
  vitals: RexyVitals | null;
  telemetry: RexyTelemetry | null; // latest entry; the feed accumulates downstream
  agents: RexyAgents | null;
}

// Subscribe to the three durable broadcasts and surface the latest of each.
export function useRexySocket(): RexySocketState {
  const [connected, setConnected] = useState(false);
  const [vitals, setVitals] = useState<RexyVitals | null>(null);
  const [telemetry, setTelemetry] = useState<RexyTelemetry | null>(null);
  const [agents, setAgents] = useState<RexyAgents | null>(null);

  useEffect(() => {
    const s = getRexySocket();

    const onConnect = () => setConnected(true);
    const onDisconnect = () => setConnected(false);
    const onVitals = (d: RexyVitals) => setVitals(d);
    const onTelemetry = (d: RexyTelemetry) => setTelemetry(d);
    const onAgents = (d: RexyAgents) => setAgents(d);

    s.on('connect', onConnect);
    s.on('disconnect', onDisconnect);
    s.on('rexy:vitals', onVitals);
    s.on('rexy:telemetry', onTelemetry);
    s.on('rexy:agents', onAgents);

    // Singleton may already be connected from a prior mount — sync immediately.
    if (s.connected) setConnected(true);

    return () => {
      // Detach this consumer's handlers; the singleton socket itself stays alive.
      s.off('connect', onConnect);
      s.off('disconnect', onDisconnect);
      s.off('rexy:vitals', onVitals);
      s.off('rexy:telemetry', onTelemetry);
      s.off('rexy:agents', onAgents);
    };
  }, []);

  return { connected, vitals, telemetry, agents };
}
